export type ThemeType = 'light' | 'dark';

export class ThemeService {
  private static instance: ThemeService;
  private currentTheme: ThemeType = 'light';
  private readonly THEME_KEY = 'vx-bms-theme';
  private listeners: Array<(theme: ThemeType) => void> = [];

  private constructor() {
    // Load saved theme from localStorage if available
    const savedTheme = localStorage.getItem(this.THEME_KEY) as ThemeType;
    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      this.currentTheme = savedTheme;
    } else {
      // Default to system preference if available
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        this.currentTheme = 'dark';
      }
    }

    // Apply the theme immediately
    this.applyTheme();

    // Listen for system theme changes
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      if (!localStorage.getItem(this.THEME_KEY)) {
        this.setTheme(e.matches ? 'dark' : 'light', false);
      }
    });
  }

  public static getInstance(): ThemeService {
    if (!ThemeService.instance) {
      ThemeService.instance = new ThemeService();
    }
    return ThemeService.instance;
  }

  public getTheme(): ThemeType {
    return this.currentTheme;
  }

  public setTheme(theme: ThemeType, savePreference = true): void {
    if (this.currentTheme !== theme) {
      this.currentTheme = theme;
      
      if (savePreference) {
        localStorage.setItem(this.THEME_KEY, theme);
      }
      
      this.applyTheme();
      this.notifyListeners();
    }
  }

  public toggleTheme(): ThemeType {
    const newTheme = this.currentTheme === 'light' ? 'dark' : 'light';
    this.setTheme(newTheme);
    return newTheme;
  }

  public addThemeChangeListener(listener: (theme: ThemeType) => void): void {
    this.listeners.push(listener);
    // Call immediately with current theme
    listener(this.currentTheme);
  }

  public removeThemeChangeListener(listener: (theme: ThemeType) => void): void {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.currentTheme));
  }

  private applyTheme(): void {
    document.documentElement.setAttribute('data-theme', this.currentTheme);
    // Vaadin/Lumo components read the `theme` attribute on <html> to switch variants
    if (this.currentTheme === 'dark') {
      document.documentElement.setAttribute('theme', 'dark');
    } else {
      document.documentElement.removeAttribute('theme');
    }

    if (this.currentTheme === 'dark') {
      document.body.classList.add('dark-theme');
      document.body.classList.remove('light-theme');
    } else {
      document.body.classList.add('light-theme');
      document.body.classList.remove('dark-theme');
    }
  }
}

// Export singleton instance
export const themeService = ThemeService.getInstance();